import type * as React from 'react';
import { type FC, useMemo } from 'react';

import type { CountryCode } from '@crehana/ts-types';
import { useMe } from '@crehana/web';
// biome-ignore lint/style/noRestrictedImports: legacy import kept for compatibility
import { Route, Switch, useLocation } from 'react-router-dom';

import { RouteHeader, RouteWrapper } from '@/views/UserDashboard/components';

import { USER_DASHBOARD_AMPLITUDE_PAGE_NAME } from '../constants';
import { useCrehanaTranslation } from '../hooks';
import type { UserDashboardLocaleKeys } from '../types';
import routes from './config';

interface RouteSwitchProps {
  countryPrefix: CountryCode | '' | null;
  onSearchClick: () => void;
  onMobileFAQsClick: () => void;
  isDark: boolean;
}

const RouteSwitch: FC<React.PropsWithChildren<RouteSwitchProps>> = ({
  countryPrefix,
  onSearchClick,
  onMobileFAQsClick,
  isDark,
}) => {
  const t = useCrehanaTranslation();
  const location = useLocation();
  const currentRoute = useMemo(() => {
    if (location.pathname === '/') {
      return routes[0];
    }
    const rootPath = location.pathname
      .split('/')
      .filter(item => item.length)[0];

    return routes.find(r => r.path.replace(/\//g, '') === rootPath);
  }, [location.pathname]);

  const { me } = useMe();

  return (
    <RouteWrapper>
      <RouteHeader
        pageTitle={t(
          `UD_${
            currentRoute ? currentRoute.key : 'MY_COURSES'
          }_TITLE` as UserDashboardLocaleKeys,
        )}
        onSearchClick={onSearchClick}
        onMobileFAQsClick={onMobileFAQsClick}
        amplitudePageName={USER_DASHBOARD_AMPLITUDE_PAGE_NAME}
        mainMenuProps={{
          customThemes:
            currentRoute?.key === 'ENROLLMENTS'
              ? {
                  light: {
                    nav: { dashboardPaddingClassNames: 'pt-24 pb-8 sm:pt-40' },
                  },
                  dark: {
                    nav: { dashboardPaddingClassNames: 'pt-24 pb-8 sm:pt-40' },
                  },
                }
              : undefined,
          layoutConfig: {
            hideUserDashboardLink: true,
            hideDiplomasLink: true,
            hideProjectsLink: true,
          },
        }}
        supportsDarkMode
      />
      <Switch>
        {routes.map(route => (
          <Route key={route.key} path={route.path} exact={route.exact}>
            <route.component
              user={me!}
              countryPrefix={countryPrefix}
              isDark={isDark}
            />
          </Route>
        ))}
      </Switch>
    </RouteWrapper>
  );
};

export default RouteSwitch;
